import React, { useState } from 'react'
import { Sidebar } from './Sidebar'
import { useLocation, Link } from 'react-router-dom'
import {
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'

interface LayoutProps {
  children: React.ReactNode
  title?: string
}

const routeLabels: { [key: string]: string } = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/vendas': 'Nova Venda',
  '/sales': 'Nova Venda',
  '/produtos': 'Produtos e Serviços',
  '/transactions': 'Transações',
  '/accounts': 'Contas Bancárias',
  '/categories': 'Categorias',
  '/bills': 'Contas a Pagar',
  '/cost-centers': 'Centros de Custo',
  '/projects': 'Projetos',
  '/cash-flow': 'Fluxo de Caixa',
  '/ai': 'Insights de IA',
  '/ai/predictions': 'Predições',
  '/ai/playground': 'AI Playground',
  '/crm': 'Leads',
  '/crm/customers': 'Clientes',
  '/crm/analytics': 'Analytics CRM',
  '/payments': 'Payment Gateway',
  '/payments/pix': 'Chaves PIX',
  '/payments/pix-transfer': 'Transferência PIX',
  '/payments/gateway-finpro': 'Gateway FinPro',
  '/integrations': 'Integrações',
  '/integrations/pluggy': 'Open Banking (Pluggy)',
  '/reports': 'Relatórios',
  '/financeiro/contas-receber': 'Contas a Receber',
  '/financeiro/transferencias': 'Transações',
  '/imports/ofx-upload': 'Importar OFX',
  '/imports/ofx': 'Importar OFX',
  '/bank-statements': 'Classificar Extratos'
}

export const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const location = useLocation()
  const { user } = useAuthStore()

  const currentTitle = title || routeLabels[location.pathname] || 'Dashboard'

  const getBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const breadcrumbs = [{ label: 'Home', path: '/' }]

    let currentPath = ''
    pathParts.forEach(part => {
      currentPath += `/${part}`
      const label = routeLabels[currentPath]
      if (label) {
        breadcrumbs.push({ label, path: currentPath })
      }
    })

    return breadcrumbs
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {!sidebarCollapsed && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-black bg-opacity-50"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out fixed lg:static inset-y-0 left-0 z-30`}>
        <Sidebar isCollapsed={false} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Bars3Icon className="w-5 h-5" />
              </button>

              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{currentTitle}</h1>

                {/* Breadcrumbs */}
                <nav className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.path}>
                      {index > 0 && <ChevronRightIcon className="w-4 h-4" />}
                      <Link
                        to={crumb.path}
                        className={`hover:text-gray-700 transition-colors ${
                          index === breadcrumbs.length - 1 ? 'text-gray-900 font-medium' : ''
                        }`}
                      >
                        {crumb.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </nav>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Search - Hidden on mobile */}
              <div className="hidden md:block relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar transações, contas..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-48 lg:w-64 xl:w-80"
                />
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={darkMode ? 'Modo Claro' : 'Modo Escuro'}
              >
                {darkMode ? (
                  <SunIcon className="w-5 h-5" />
                ) : (
                  <MoonIcon className="w-5 h-5" />
                )}
              </button>

              {/* Notifications */}
              <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <BellIcon className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              </button>

              {/* Profile */}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-sm font-bold text-white">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-700">{user?.name || 'Usuário'}</p>
                  <p className="text-xs text-gray-500">Administrador</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}